// The grain of the page: specks, short fibers, and clumps on a grid of hard
// cells, as the path of one SVG tile. Pure. A tile wraps, so its copies meet
// with no seam. The mottle makes some regions of a tile dense and some thin,
// so the eye does not find an even field.

import {
  GRAIN_CELL_PX,
  GRAIN_CLUMP_REACH_CELLS,
  GRAIN_CLUMP_SPECKS,
  GRAIN_CLUMPS_PER_1000,
  GRAIN_FIBER_BEND_ODDS,
  GRAIN_FIBER_CELLS,
  GRAIN_FIBERS_PER_1000,
  GRAIN_MOTTLE_FLOOR,
  GRAIN_MOTTLE_GRID,
  GRAIN_SPECKS_PER_1000,
} from '../constants';
import type { Rng } from './rng';

type Cell = readonly [column: number, row: number];

const between = (rng: Rng, [least, most]: readonly [number, number]): number =>
  least + rng.int(most - least + 1);

/** The chance that a mark at a cell stays: random values on a coarse grid that wraps, with straight lines between them. */
const createMottle = (rng: Rng, sideCells: number): ((cell: Cell) => number) => {
  const values = Array.from({ length: GRAIN_MOTTLE_GRID * GRAIN_MOTTLE_GRID }, () => rng.next());
  const at = (column: number, row: number): number =>
    values[(row % GRAIN_MOTTLE_GRID) * GRAIN_MOTTLE_GRID + (column % GRAIN_MOTTLE_GRID)] ?? 0;
  return ([column, row]) => {
    const x = (column / sideCells) * GRAIN_MOTTLE_GRID;
    const y = (row / sideCells) * GRAIN_MOTTLE_GRID;
    const left = Math.floor(x);
    const top = Math.floor(y);
    const upper = at(left, top) + (at(left + 1, top) - at(left, top)) * (x - left);
    const lower = at(left, top + 1) + (at(left + 1, top + 1) - at(left, top + 1)) * (x - left);
    const chance = upper + (lower - upper) * (y - top);
    return GRAIN_MOTTLE_FLOOR + (1 - GRAIN_MOTTLE_FLOOR) * chance;
  };
};

/** The cells of one tile that are on. A cell over the edge comes back in at the other side. */
export const grainCells = (rng: Rng, sideCells: number): Cell[] => {
  const mottle = createMottle(rng, sideCells);
  const thousands = (sideCells * sideCells) / 1000;
  const cells: Cell[] = [];
  const add = (column: number, row: number): void => {
    cells.push([(column + sideCells) % sideCells, (row + sideCells) % sideCells]);
  };
  const place = (count: number, draw: (center: Cell) => void): void => {
    for (let i = 0; i < Math.round(count * thousands); i += 1) {
      const center: Cell = [rng.int(sideCells), rng.int(sideCells)];
      if (rng.next() < mottle(center)) draw(center);
    }
  };

  place(GRAIN_SPECKS_PER_1000, ([column, row]) => add(column, row));
  place(GRAIN_FIBERS_PER_1000, ([column, row]) => {
    const length = between(rng, GRAIN_FIBER_CELLS);
    const level = rng.int(2) === 0;
    const bendAt = rng.int(GRAIN_FIBER_BEND_ODDS) === 0 ? Math.floor(length / 2) : length;
    for (let step = 0; step < length; step += 1) {
      const shift = step >= bendAt ? 1 : 0;
      if (level) add(column + step, row + shift);
      else add(column + shift, row + step);
    }
  });
  place(GRAIN_CLUMPS_PER_1000, ([column, row]) => {
    const reach = GRAIN_CLUMP_REACH_CELLS * 2 + 1;
    for (let speck = 0; speck < between(rng, GRAIN_CLUMP_SPECKS); speck += 1) {
      add(
        column + rng.int(reach) - GRAIN_CLUMP_REACH_CELLS,
        row + rng.int(reach) - GRAIN_CLUMP_REACH_CELLS,
      );
    }
  });
  return cells;
};

/** One tile as an SVG document. The color of the cells does not matter: the tile is a mask. */
export const grainTileSvg = (rng: Rng, sideCells: number): string => {
  const side = sideCells * GRAIN_CELL_PX;
  const square = `h${GRAIN_CELL_PX}v${GRAIN_CELL_PX}h-${GRAIN_CELL_PX}z`;
  const path = grainCells(rng, sideCells)
    .map(([column, row]) => `M${column * GRAIN_CELL_PX} ${row * GRAIN_CELL_PX}${square}`)
    .join('');
  return `<svg xmlns='http://www.w3.org/2000/svg' width='${side}' height='${side}' shape-rendering='crispEdges'><path d='${path}'/></svg>`;
};
